import { Fragment, useCallback, useRef, useState, useMemo } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { formatDate, debounce } from '../../utils/index';
import TransporterModal from './TransporterModal';
import './DphRFQDetails.scss';

const PAGE_SIZE = 10;

const TripsTable = ({ trips, onMoreClick }) => (
    <table className="trips-table">
        <thead>
            <tr>
                <th>From</th>
                <th>To</th>
                <th>Route Code</th>
                <th>Vehicle Type</th>
                <th>Transporters & Price</th>
            </tr>
        </thead>

        <tbody>
            {trips.map((trip, index) => {
                const first = trip.transporters?.[0];
                const extraCount = trip.transporters.length - 1;

                return (
                    <tr key={index}>
                        <td>{trip.from}</td>
                        <td>{trip.to}</td>
                        <td>{trip.route_code || '--'}</td>
                        <td>{trip.vehicle_type_name}</td>
                        <td>
                            {first && (
                                <>
                                    <strong>{first.transporter_name}</strong>
                                    : ₹{first.price.toLocaleString()}
                                </>
                            )}
                            {extraCount > 0 && (
                                <span
                                    className="more-link"
                                    onClick={() => onMoreClick(trip.transporters)}
                                >
                                    {' '}+{extraCount} more
                                </span>
                            )}
                        </td>
                    </tr>
                );
            })}
        </tbody>
    </table>
);

const getPageNumbers = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
    if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
    return [1, '...', current - 1, current, current + 1, '...', total];
};

const DphRFQDetails = ({ rfqList = [], totalCount, onBack, onPageChange }) => {
    const [expandedIndex, setExpandedIndex] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [showTransporterModal, setShowTransporterModal] = useState(false);
    const [selectedTransporters, setSelectedTransporters] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);

    const totalPages = Math.ceil((totalCount || 0) / PAGE_SIZE);
    const startEntry = totalCount ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
    const endEntry = Math.min(currentPage * PAGE_SIZE, totalCount || 0);

    const debouncedSearch = useRef(
        debounce((value) => {
            setCurrentPage(1);
            onPageChange?.(1, value);
        }, 500)
    ).current;

    const toggleRow = useCallback((index) => {
        setExpandedIndex(prev => (prev === index ? null : index));
    }, []);

    const openTransporterModal = useCallback((transporters) => {
        setSelectedTransporters(transporters);
        setShowTransporterModal(true);
    }, []);

    const closeTransporterModal = useCallback(() => {
        setShowTransporterModal(false);
        setSelectedTransporters([]);
    }, []);

    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
        setExpandedIndex(null);
        onPageChange?.(page, searchText);
    }, [onPageChange, searchText]);

    const pageNumbers = useMemo(() => getPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

    return (
        <div className="rfq-details">
            <button className="back-btn" onClick={onBack}>
                ← Back to DPH Dashboard
            </button>

            <h2>RFQ Details</h2>

            <div className="search-wrapper">
                <input
                    type="text"
                    placeholder="Search RFQs"
                    value={searchText}
                    onChange={(e) => {
                        setSearchText(e.target.value);
                        debouncedSearch(e.target.value);
                    }}
                />
            </div>

            <table className="rfq-table">
                <thead>
                    <tr>
                        <th>Sl No</th>
                        <th>RFQ Name</th>
                        <th>RFQ Number</th>
                        <th>Contract valid Till</th>
                        <th>DPH valid Till</th>
                        <th>Total Routes</th>
                        <th></th>
                    </tr>
                </thead>

                <tbody>
                    {rfqList.length > 0 ? (
                        rfqList.map((rfq, index) => {
                            const isExpanded = expandedIndex === index;
                            const slNo = (currentPage - 1) * PAGE_SIZE + index + 1;
                            return (
                                <Fragment key={rfq.rfq_number}>
                                    <tr>
                                        <td>{slNo}</td>
                                        <td
                                            style={{
                                                overflowWrap: 'break-word',
                                                wordBreak: 'break-word',
                                                maxWidth: '200px',
                                            }}
                                        >{rfq.rfq_name}</td>
                                        <td>{rfq.rfq_number}</td>
                                        <td>{formatDate(rfq.contract_tenure_to)}</td>
                                        <td>{formatDate(rfq.dph_date_to)}</td>
                                        <td>{rfq.total_bids_count}</td>
                                        <td>
                                            {rfq.trips?.length > 0 && (
                                                <button
                                                    className="expand-btn"
                                                    onClick={() => toggleRow(index)}
                                                >
                                                    {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                                </button>
                                            )}
                                        </td>
                                    </tr>

                                    {isExpanded && rfq.trips?.length > 0 && (
                                        <tr className="expanded-row">
                                            <td colSpan="7">
                                                <TripsTable
                                                    trips={rfq.trips}
                                                    onMoreClick={openTransporterModal}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan="7" className="no-data">No RFQs found</td>
                        </tr>
                    )}
                </tbody>
            </table>

            {totalPages > 1 && (
                <div className="pagination">
                    <span className="pagination-info">
                        Showing {startEntry}–{endEntry} of {totalCount}
                    </span>

                    <div className="pagination-controls">
                        <button
                            className="page-btn"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            ‹ Prev
                        </button>

                        {pageNumbers.map((page, i) =>
                            page === '...' ? (
                                <span key={`ellipsis-${i}`} className="page-ellipsis">…</span>
                            ) : (
                                <button
                                    key={page}
                                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                                    onClick={() => handlePageChange(page)}
                                >
                                    {page}
                                </button>
                            )
                        )}

                        <button
                            className="page-btn"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next ›
                        </button>
                    </div>
                </div>
            )}

            <TransporterModal
                visible={showTransporterModal}
                onClose={closeTransporterModal}
                transporters={selectedTransporters}
            />
        </div>
    );
};

export default DphRFQDetails;
